import { buildNamedSearchCriteria, buildSortArray } from '@/data/utils';
import { Deployment } from '@/types/deployment';
import { HouseResponse } from '@/types/house.types';
import { makeFetchOrThrow } from '@/data/helpers/makeFetch';
import { Method } from '@/data/enums/method';
import { PaginatedResponse } from '@/types/apiTypes';
import { QueryKey } from '@/data/enums/queryKeys';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { Services } from '@/data/enums/services';
import { useAppContext } from '@/providers/AppContextProvider/AppContextProvider';

export type HouseSearchParams = {
    search?: string;
};

type GetHouseListProps = {
    deployment: Deployment;
    searchParams: HouseSearchParams;
    token: string;
};

const getHouseList = async ({
    deployment,
    searchParams,
    token,
}: GetHouseListProps): Promise<PaginatedResponse<HouseResponse>> => {
    if (!token) {
        throw new Error('No token found. Please contact your administrator.');
    }

    // POST-as-search: backend search endpoint requires POST with RestQuery body
    return makeFetchOrThrow<PaginatedResponse<HouseResponse>>({
        apiPath: 'houses/search',
        apiService: Services.SellerPortal,
        body: {
            criteria: [],
            namedCriteria: buildNamedSearchCriteria('houseSearch', searchParams.search),
            pageSize: 100,
            sort: buildSortArray({ direction: 'ASC', property: 'name' }),
        },
        deployment,
        method: Method.Post,
        token,
    });
};

type GetHouseListOptionsProps = {
    deployment: Deployment;
    searchParams?: HouseSearchParams;
    token: string;
};

export const getHouseListOptions = ({ deployment, searchParams = {}, token }: GetHouseListOptionsProps) => {
    return queryOptions<PaginatedResponse<HouseResponse>>({
        enabled: Boolean(token),
        queryFn: () => getHouseList({ deployment, searchParams, token }),
        queryKey: [QueryKey.HouseList, searchParams],
    });
};

export const useGetHouseList = (searchParams?: HouseSearchParams) => {
    const { deployment, token } = useAppContext();

    return useQuery(getHouseListOptions({ deployment, searchParams, token }));
};
